import { type FC, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import { HpSharedFeaturedModuleContent } from './HpSharedFeaturedModuleContent.tsx';
import { HpSharedFeaturedModuleContentVariant } from './HpSharedFeaturedModuleContentVariant.tsx';
import {
    useIsHpSaleModuleVariant,
    trackHpSaleModuleTest,
} from '../helpers/hpSaleModuleTestHelpers.ts';

import { type HpSharedFeaturedModule_viewer$key } from './__generated__/HpSharedFeaturedModule_viewer.graphql.ts';
import { type HpSharedFeaturedModule_user$key } from './__generated__/HpSharedFeaturedModule_user.graphql.ts';

const viewerFragment = graphql`
    fragment HpSharedFeaturedModule_viewer on Viewer {
        ...HpSharedFeaturedModuleContent_viewer @skip(if: $isHpSaleModuleVariant)
        ...HpSharedFeaturedModuleContentVariant_viewer @include(if: $isHpSaleModuleVariant)
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModule_user on User {
        ...HpSharedFeaturedModuleContent_user @skip(if: $isHpSaleModuleVariant)
        ...HpSharedFeaturedModuleContentVariant_user @include(if: $isHpSaleModuleVariant)
    }
`;

export const HpSharedFeaturedModule: FC<{
    viewer: HpSharedFeaturedModule_viewer$key | null;
    user?: HpSharedFeaturedModule_user$key | null;
    isClient: boolean;
}> = ({ viewer: viewerRef, user: userRef, isClient }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const isVariant = useIsHpSaleModuleVariant();

    const onHpSaleModuleVisibility = useCallback(({ isVisible }: { isVisible: boolean }) => {
        if (isVisible) {
            trackHpSaleModuleTest();
        }
    }, []);

    if (!isClient) {
        return null;
    }

    return (
        <>
            <VisibilityTracker onVisibilityChange={onHpSaleModuleVisibility} />
            {isVariant ? (
                <HpSharedFeaturedModuleContentVariant viewer={viewer} user={user} />
            ) : (
                <HpSharedFeaturedModuleContent viewer={viewer} user={user} />
            )}
        </>
    );
};
